import React from 'react';
import { useTranslation } from 'react-i18next';

import { Field, Button, Text, Select, type SelectOption, Sheet, useSheetDismiss } from '@/nova/components';
import { createInvite, getAffiliateError } from '../../api';
import type { OrgInvite, OrgInviteInput } from '../../types';
import { isOpenableHttpUrl } from '../../url-safety';
import { FormCol, FieldGroup, Helper } from '../formControls.styles';
import { LinkBox, LinkText, ModeTabs, ModeTab } from './InviteLinkSheet.styles';

export type InviteLinkSheetProps = {
  casinoInstanceId: string;
  onClose: () => void;
  /** Called after an invite is successfully created, so the page can refresh its pending-invite list. */
  onCreated: () => void;
  onToast: (message: string) => void;
};

const DEFAULT_EXPIRES_DAYS = 7;
/** How many partners can register with one shareable link: the vendor's ceiling. Without it the vendor default (1)
 *  would close an "anyone can join" link after the first sign-up. Email invites keep the default (one person). */
const LINK_MAX_USES = 100;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Predefined invite lifetimes; the value is the `expires_days` count sent to the API. */
const EXPIRY_DAYS = ['1', '7', '14', '30'] as const;

type InviteMode = 'email' | 'link';

/**
 * "Invite an affiliate": two ways to bring a partner in, chosen with a segmented control (Email is the
 * default). EMAIL binds the invite to a specific address (they show as a pending invitation at the top
 * of the roster until they join); SHAREABLE LINK mints an open self-registration link for anyone.
 * Either way the backend composes a ready-to-share `link`, surfaced here as a copy fallback. The
 * list of existing pending invites now lives in the roster, not in this modal.
 */
export const InviteLinkSheet = ({ casinoInstanceId, onClose, onCreated, onToast }: InviteLinkSheetProps): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle } = useSheetDismiss(onClose);

  const [mode, setMode] = React.useState<InviteMode>('email');
  const [email, setEmail] = React.useState('');
  const [expiresDays, setExpiresDays] = React.useState(String(DEFAULT_EXPIRES_DAYS));
  const [generating, setGenerating] = React.useState(false);
  const [minted, setMinted] = React.useState<OrgInvite | null>(null);

  const emailTrim = email.trim();
  const emailValid = EMAIL_RE.test(emailTrim);
  const days = parseInt(expiresDays, 10);
  // Email mode needs a valid address; link mode has nothing to validate.
  const canGenerate = !generating && (mode === 'link' || emailValid);

  const copy = React.useCallback(
    (link: string) => {
      void navigator.clipboard?.writeText(link).then(
        () => onToast(t('Invite link copied')),
        () => onToast(t('Could not copy')),
      );
    },
    [onToast, t],
  );

  // Switching mode clears a previously minted link so a stale one isn't shown under the other mode.
  const selectMode = React.useCallback((next: InviteMode) => {
    setMode(next);
    setMinted(null);
  }, []);

  const onGenerate = React.useCallback(async () => {
    if (!canGenerate) return;
    setGenerating(true);
    try {
      const input: OrgInviteInput = {
        ...(mode === 'email' ? { email: emailTrim } : { max_uses: LINK_MAX_USES }),
        ...(Number.isFinite(days) && days > 0 ? { expires_days: days } : {}),
      };
      const invite = await createInvite(casinoInstanceId, input);
      setMinted(invite);
      onCreated();
      onToast(mode === 'email' ? t('Invitation prepared for {{email}}', { email: emailTrim }) : t('Invite link created'));
      // Clear the address after an email invite so re-clicking Send does not mint a duplicate for the same
      // partner (email mode requires a valid address, so an empty field disables the CTA until the next one).
      if (mode === 'email') setEmail('');
    } catch (err: unknown) {
      onToast(getAffiliateError(err).message);
    } finally {
      setGenerating(false);
    }
  }, [canGenerate, mode, emailTrim, days, casinoInstanceId, onCreated, onToast, t]);

  // Literal-keyed so the i18n extractor harvests each mode label.
  const modeLabels: Record<InviteMode, string> = { email: t('Email invite'), link: t('Shareable link') };
  // Literal t() calls so the extractor harvests each option key (Select can't be statically scanned).
  const expiryLabels: Record<string, string> = { '1': t('1 day'), '7': t('7 days'), '14': t('14 days'), '30': t('30 days') };
  const expiryOptions: SelectOption[] = EXPIRY_DAYS.map((d) => ({ value: d, label: expiryLabels[d] }));

  return (
    <Sheet
      title={t('Invite an affiliate')}
      ariaLabel={t('Invite an affiliate')}
      onClose={onClose}
      closeHandle={closeHandle}
      busy={generating}
    >
      <FormCol>
        <ModeTabs role="tablist" aria-label={t('Invite mode')}>
          {(['email', 'link'] as const).map((value) => (
            <ModeTab
              key={value}
              type="button"
              role="tab"
              $active={mode === value}
              aria-selected={mode === value}
              onClick={() => selectMode(value)}
            >
              <Text text={modeLabels[value]} tag="span" />
            </ModeTab>
          ))}
        </ModeTabs>

        {mode === 'email' ? (
          <>
            <Helper text="Invite a specific partner by email. They show as a pending invitation until they join." tag="span" />
            <Field label={t('Email')} value={email} placeholder="promo@partner.com" onChange={setEmail} />
          </>
        ) : (
          <Helper
            text="Generate a link to share with partners. Up to 100 people can register with it as affiliates in your program."
            tag="span"
          />
        )}

        <Select label={t('Invite expires')} value={expiresDays} onChange={setExpiresDays} options={expiryOptions} />
        <Button
          label={mode === 'email' ? t('Send invite') : t('Generate invite link')}
          variant="primary"
          icon={mode === 'email' ? 'mail' : 'plus'}
          onClick={onGenerate}
          disabled={!canGenerate}
        />

        {minted && isOpenableHttpUrl(minted.link) ? (
          <FieldGroup>
            {mode === 'email' ? <Helper text="Share this link if the invite email does not arrive:" tag="span" /> : null}
            <LinkBox>
              <LinkText>{minted.link}</LinkText>
              <Button label={t('Copy')} variant="outline" icon="copy" onClick={() => copy(minted.link)} />
            </LinkBox>
          </FieldGroup>
        ) : null}
      </FormCol>
    </Sheet>
  );
};

export default InviteLinkSheet;
